import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SortingState } from '@tanstack/react-table'

export type DashboardFilters = {
  search: string
  signal: string[]
  vendor: string[]
  product_type: string[]
  status: string[]
  location: string[]
  tags_include: string[]
  tags_exclude: string[]
  cov_min: number | null
  cov_max: number | null
  // "Check history" flag (lifetime_spike): 'flagged' and/or 'not_flagged'; empty = all rows.
  spike: string[]
  // "Check history (all)" flag (lifetime_spike_all), same values.
  spike_all: string[]
}

// Default filters applied every time the dashboard mounts and whenever the
// user clicks "Clear all" in the filter rail. Product spec: focus the buyer
// on the actionable subset — active, on-strategy items that need an order.
export const DEFAULT_DASHBOARD_FILTERS: DashboardFilters = {
  search: '',
  signal: ['order'],
  vendor: [],
  product_type: [],
  status: ['active'],
  location: [],
  tags_include: ['DHCORE'],
  tags_exclude: ['inv_exclude'],
  cov_min: null,
  cov_max: null,
  spike: [],
  spike_all: [],
}

// Truly empty state — used by tests that need to start from a clean slate
// without inheriting product defaults.
export const EMPTY_DASHBOARD_FILTERS: DashboardFilters = {
  search: '',
  signal: [],
  vendor: [],
  product_type: [],
  status: [],
  location: [],
  tags_include: [],
  tags_exclude: [],
  cov_min: null,
  cov_max: null,
  spike: [],
  spike_all: [],
}

// Canonical column order. Pinned columns must be at the front of the array
// AND listed in `columnPinning.left` for TanStack to render them sticky.
export const DEFAULT_DASHBOARD_COLUMN_ORDER: string[] = [
  'signal_stripe',
  'select',
  'product_title',
  'barcode',
  'sku',
  'variant_title',
  'product_type',
  'vendor',
  'status',
  'on_hand',
  'incoming',
  'action_signal',
  'recommended_qty',
  'qty_sold_30d',
  'qty_sold_60d',
  'qty_sold_90d',
  'qty_sold_120d',
  'last_in_stock_60d',
  'in_stock_days_60',
  'true_velocity',
  'lifetime_rate_monthly',
  'recent_vs_lifetime',
  'product_lifetime_rate_monthly',
  'product_recent_vs_lifetime',
  'lifetime_spike',
  'lifetime_spike_all',
  'seasonal_multiplier',
  'predicted_demand',
  'days_coverage',
  'age_days',
  'return_rate',
  'marketing_multiplier',
  'unit_cost',
  'cost_of_order_dollars',
]

export const DEFAULT_DASHBOARD_PINNING = { left: ['signal_stripe', 'select', 'product_title'] }

export const DEFAULT_DASHBOARD_COLUMN_VISIBILITY: Record<string, boolean> = {
  signal_stripe: true,
  select: true,
  product_title: true,
  barcode: true,
  sku: true,
  variant_title: true,
  product_type: true,
  vendor: true,
  status: false,
  on_hand: true,
  incoming: true,
  action_signal: true,
  recommended_qty: true,
  qty_sold_30d: true,
  qty_sold_60d: true,
  qty_sold_90d: true,
  qty_sold_120d: true,
  last_in_stock_60d: true,
  in_stock_days_60: true,
  true_velocity: true,
  // Lifetime context: diagnostic, hidden by default like the per-location
  // stock columns, so the default grid keeps its width.
  lifetime_rate_monthly: false,
  recent_vs_lifetime: false,
  product_lifetime_rate_monthly: false,
  product_recent_vs_lifetime: false,
  lifetime_spike: false,
  lifetime_spike_all: false,
  seasonal_multiplier: true,
  predicted_demand: true,
  days_coverage: true,
  age_days: true,
  return_rate: true,
  marketing_multiplier: true,
  unit_cost: true,
  cost_of_order_dollars: true,
}

const PRODUCT_LIFETIME_COLUMNS = ['product_lifetime_rate_monthly', 'product_recent_vs_lifetime']
const LIFETIME_COLUMNS = [
  'lifetime_rate_monthly', 'recent_vs_lifetime', ...PRODUCT_LIFETIME_COLUMNS, 'lifetime_spike',
]
const LIFETIME_COLUMNS_HIDDEN = Object.fromEntries(LIFETIME_COLUMNS.map((c) => [c, false]))

// Splice the lifetime columns into a stored order right after true_velocity
// (or at the end if the user's order has lost it).
function withLifetimeColumns(stored: string[]): string[] {
  const order = stored.filter((c) => !LIFETIME_COLUMNS.includes(c))
  const vIdx = order.indexOf('true_velocity')
  if (vIdx >= 0) order.splice(vIdx + 1, 0, ...LIFETIME_COLUMNS)
  else order.push(...LIFETIME_COLUMNS)
  return order
}

// v5: splice the product-level columns in right after vs usual (or with the
// other lifetime columns if the user's order has lost it).
function withProductLifetimeColumns(stored: string[]): string[] {
  const order = stored.filter((c) => !PRODUCT_LIFETIME_COLUMNS.includes(c))
  const idx = order.indexOf('recent_vs_lifetime')
  if (idx < 0) return withLifetimeColumns(order)
  order.splice(idx + 1, 0, ...PRODUCT_LIFETIME_COLUMNS)
  return order
}

// v6: In-stock days (60) — True Velocity's denominator — sits right before
// True Velocity (or at the end if the user's order has lost it), visible.
function withInStockDaysColumn(stored: string[]): string[] {
  const order = stored.filter((c) => c !== 'in_stock_days_60')
  const vIdx = order.indexOf('true_velocity')
  if (vIdx >= 0) order.splice(vIdx, 0, 'in_stock_days_60')
  else order.push('in_stock_days_60')
  return order
}

// v7: Check history (all) sits right after Check history (or at the end if
// the user's order has lost it), hidden.
function withCheckHistoryAllColumn(stored: string[]): string[] {
  const order = stored.filter((c) => c !== 'lifetime_spike_all')
  const idx = order.indexOf('lifetime_spike')
  if (idx >= 0) order.splice(idx + 1, 0, 'lifetime_spike_all')
  else order.push('lifetime_spike_all')
  return order
}

function migrateToV5(persistedState: unknown, version: number): unknown {
  if (version < 2) {
    return {
      ...(persistedState as object),
      columnOrder: [...DEFAULT_DASHBOARD_COLUMN_ORDER],
      columnPinning: { left: [...DEFAULT_DASHBOARD_PINNING.left] },
      columnVisibility: { ...DEFAULT_DASHBOARD_COLUMN_VISIBILITY },
    }
  }
  if (version < 3) {
    // Splice barcode + sku into the stored order right after
    // product_title; default them to visible. Preserve everything
    // else the user has customized.
    const s = persistedState as { columnOrder?: string[]; columnVisibility?: Record<string, boolean> }
    const order = (s.columnOrder ?? [...DEFAULT_DASHBOARD_COLUMN_ORDER]).filter(
      (c) => c !== 'barcode' && c !== 'sku'
    )
    const pIdx = order.indexOf('product_title')
    if (pIdx >= 0) order.splice(pIdx + 1, 0, 'barcode', 'sku')
    else order.push('barcode', 'sku')
    return {
      ...(persistedState as object),
      columnOrder: withLifetimeColumns(order),
      columnVisibility: { ...(s.columnVisibility ?? {}), barcode: true, sku: true, ...LIFETIME_COLUMNS_HIDDEN },
    }
  }
  if (version < 4) {
    const s = persistedState as { columnOrder?: string[]; columnVisibility?: Record<string, boolean> }
    return {
      ...(persistedState as object),
      columnOrder: withLifetimeColumns(s.columnOrder ?? [...DEFAULT_DASHBOARD_COLUMN_ORDER]),
      columnVisibility: { ...(s.columnVisibility ?? {}), ...LIFETIME_COLUMNS_HIDDEN },
    }
  }
  if (version < 5) {
    const s = persistedState as { columnOrder?: string[]; columnVisibility?: Record<string, boolean> }
    return {
      ...(persistedState as object),
      columnOrder: withProductLifetimeColumns(s.columnOrder ?? [...DEFAULT_DASHBOARD_COLUMN_ORDER]),
      columnVisibility: {
        ...(s.columnVisibility ?? {}),
        product_lifetime_rate_monthly: false,
        product_recent_vs_lifetime: false,
      },
    }
  }
  return persistedState
}

export type SavedView = {
  name: string
  filters: DashboardFilters
  sort: SortingState
}

type DashboardState = {
  filters: DashboardFilters
  selectedRowIds: Set<string>
  savedViews: SavedView[]
  activeViewName: string | null
  columnVisibility: Record<string, boolean>
  columnOrder: string[]
  columnPinning: { left: string[] }
  columnSizing: Record<string, number>
  ptColumnSizing: Record<string, number>
  railCollapsed: boolean
  setFilters: (f: Partial<DashboardFilters>) => void
  clearFilters: () => void
  toggleSelected: (id: string) => void
  toggleSelectAll: (ids: string[]) => void
  clearSelected: () => void
  saveCurrentAsView: (name: string, sort: SortingState) => void
  applyView: (name: string) => void
  deleteView: (name: string) => void
  setColumnVisible: (id: string, visible: boolean) => void
  resetColumnVisibility: () => void
  setColumnOrder: (order: string[]) => void
  togglePinned: (id: string) => void
  setColumnSizing: (sizing: Record<string, number>) => void
  setPtColumnSizing: (sizing: Record<string, number>) => void
  toggleRailCollapsed: () => void
  resetColumnLayout: () => void
}

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set) => ({
      filters: { ...DEFAULT_DASHBOARD_FILTERS },
      selectedRowIds: new Set(),
      savedViews: [],
      activeViewName: null,
      columnVisibility: { ...DEFAULT_DASHBOARD_COLUMN_VISIBILITY },
      columnOrder: [...DEFAULT_DASHBOARD_COLUMN_ORDER],
      columnPinning: { left: [...DEFAULT_DASHBOARD_PINNING.left] },
      columnSizing: {},
      ptColumnSizing: {},
      railCollapsed: false,

      setFilters: (f) =>
        set((s) => ({ filters: { ...s.filters, ...f }, activeViewName: null })),

      // "Clear all" wipes every filter — including the product defaults.
      // Mount-time defaults (Order Now / Active / DHCORE) come back on the
      // next page load, not on Clear.
      clearFilters: () =>
        set({ filters: { ...EMPTY_DASHBOARD_FILTERS }, activeViewName: null }),

      toggleSelected: (id) =>
        set((s) => {
          const next = new Set(s.selectedRowIds)
          if (next.has(id)) {
            next.delete(id)
          } else {
            next.add(id)
          }
          return { selectedRowIds: next }
        }),

      toggleSelectAll: (ids) =>
        set((s) => {
          const allSelected = ids.every((id) => s.selectedRowIds.has(id))
          return { selectedRowIds: allSelected ? new Set() : new Set(ids) }
        }),

      clearSelected: () => set({ selectedRowIds: new Set() }),

      saveCurrentAsView: (name, sort) =>
        set((s) => {
          const existing = s.savedViews.filter((v) => v.name !== name)
          return {
            savedViews: [...existing, { name, filters: { ...s.filters }, sort }],
            activeViewName: name,
          }
        }),

      applyView: (name) =>
        set((s) => {
          const view = s.savedViews.find((v) => v.name === name)
          if (!view) return {}
          // Views saved before a filter existed lack its key; treat it as unset.
          return { filters: { ...EMPTY_DASHBOARD_FILTERS, ...view.filters }, activeViewName: name }
        }),

      deleteView: (name) =>
        set((s) => ({
          savedViews: s.savedViews.filter((v) => v.name !== name),
          activeViewName: s.activeViewName === name ? null : s.activeViewName,
        })),

      setColumnVisible: (id, visible) =>
        set((s) => ({
          columnVisibility: { ...s.columnVisibility, [id]: visible },
        })),

      resetColumnVisibility: () =>
        set({ columnVisibility: { ...DEFAULT_DASHBOARD_COLUMN_VISIBILITY } }),

      setColumnOrder: (order) => set({ columnOrder: order }),

      togglePinned: (id) =>
        set((s) => {
          const left = s.columnPinning.left
          const next = left.includes(id) ? left.filter((c) => c !== id) : [...left, id]
          return { columnPinning: { left: next } }
        }),

      setColumnSizing: (sizing) => set({ columnSizing: sizing }),

      setPtColumnSizing: (sizing) => set({ ptColumnSizing: sizing }),

      toggleRailCollapsed: () => set((s) => ({ railCollapsed: !s.railCollapsed })),

      resetColumnLayout: () =>
        set({
          columnOrder: [...DEFAULT_DASHBOARD_COLUMN_ORDER],
          columnPinning: { left: [...DEFAULT_DASHBOARD_PINNING.left] },
          columnVisibility: { ...DEFAULT_DASHBOARD_COLUMN_VISIBILITY },
          columnSizing: {},
        }),
    }),
    {
      name: 'dashboard-ui',
      version: 7,
      // Migrate stored layouts that pre-date the column-order/pinning additions
      // so users don't get a half-broken table from old localStorage.
      // v3 (May 2026): added barcode + sku columns next to product_title.
      // v4 (Oct 2026): added the lifetime columns after true_velocity, hidden.
      // v5 (Oct 2026): added the product-level lifetime columns, hidden.
      // v6 (Oct 2026): added In-stock days (60) before true_velocity, visible.
      // v7 (Oct 2026): added Check history (all) after Check history, hidden.
      migrate: (persistedState, version) => {
        type Layout = { columnOrder?: string[]; columnVisibility?: Record<string, boolean> }
        let s = migrateToV5(persistedState, version) as Layout
        if (version < 6) {
          s = {
            ...s,
            columnOrder: withInStockDaysColumn(s.columnOrder ?? [...DEFAULT_DASHBOARD_COLUMN_ORDER]),
            columnVisibility: { ...(s.columnVisibility ?? {}), in_stock_days_60: true },
          }
        }
        if (version >= 7) return s
        return {
          ...s,
          columnOrder: withCheckHistoryAllColumn(s.columnOrder ?? [...DEFAULT_DASHBOARD_COLUMN_ORDER]),
          columnVisibility: { ...(s.columnVisibility ?? {}), lifetime_spike_all: false },
        }
      },
      partialize: (s) => ({
        savedViews: s.savedViews,
        columnVisibility: s.columnVisibility,
        columnOrder: s.columnOrder,
        columnPinning: s.columnPinning,
        columnSizing: s.columnSizing,
        ptColumnSizing: s.ptColumnSizing,
        railCollapsed: s.railCollapsed,
      }),
    }
  )
)
